import { z } from "zod";
import apiClient from "../../../shared/api/client";

export type QuotaPeriod = "daily" | "weekly" | "monthly" | "yearly";

const platformQuotaSchema = z.object({
  platform: z.string(),
  used: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative().nullable(),
  available: z.number().int().nonnegative().nullable(),
  isUnlimited: z.boolean(),
});

const cronExecutionSchema = z.object({
  executedAt: z.string(),
  workspaceName: z.string(),
  platform: z.string(),
  tokensRefreshed: z.number().int().nonnegative(),
  triggeredBy: z.string(),
  success: z.boolean(),
  message: z.string().optional().nullable(),
  durationMs: z.number().int().nonnegative().optional(),
  userId: z.number().int().positive().optional(),
  userName: z.string().optional(),
  userEmail: z.string().optional(),
  userCreatedAt: z.string().optional(),
});

const cronFrequencySchema = z.object({
  allowedMinutes: z.array(z.number().int().positive()),
  selectedMinutes: z.number().int().positive(),
  executions: z.array(cronExecutionSchema),
  executionsTotal: z.number().int().nonnegative(),
  executionsPage: z.number().int().positive(),
  executionsPageSize: z.number().int().positive(),
  pendingJobs: z.number().int().nonnegative(),
  processedToday: z.number().int().nonnegative(),
  quotaPeriod: z.enum(["daily", "weekly", "monthly", "yearly"]),
  quotas: z.array(platformQuotaSchema),
});

export type CronFrequency = z.infer<typeof cronFrequencySchema>;
export type PlatformQuota = z.infer<typeof platformQuotaSchema>;
export type CronExecution = z.infer<typeof cronExecutionSchema>;

export const fetchCronSettings = async (
  page = 1,
  pageSize = 10,
  filters?: { wallet?: string; platform?: string; success?: string; from?: string; to?: string },
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  quotaPeriod: QuotaPeriod = "daily"
): Promise<CronFrequency> => {
  const response = await apiClient.get("/maintenance/settings/cron", {
    params: {
      page,
      pageSize,
      wallet: filters?.wallet,
      platform: filters?.platform,
      success: filters?.success,
      from: filters?.from,
      to: filters?.to,
      sortBy,
      sortOrder,
      quotaPeriod,
    },
  });
  return cronFrequencySchema.parse(response.data);
};

type UpdateCronFrequencyInput = {
  minutes: number;
};

export const updateCronFrequency = async (input: UpdateCronFrequencyInput) => {
  await apiClient.patch("/maintenance/settings/cron", {
    minutes: input.minutes,
  });
};
